import { useAuth } from '~/context/auth';

interface ApiClientOptions {
  baseURL: string;
  getToken: () => string | null;
  getRefreshToken: () => string | null;
  getClerkToken: () => Promise<string | null>;
  onTokenRefresh: (tokenData: any) => void;
  onSignOut: () => void;
  updateApolloToken?: (token: string) => void;
}

export class ApiClient {
  private baseURL: string;
  private getToken: () => string | null;
  private getRefreshToken: () => string | null;
  private getClerkToken: () => Promise<string | null>;
  private onTokenRefresh: (tokenData: any) => void;
  private onSignOut: () => void;
  private updateApolloToken?: (token: string) => void;

  constructor(options: ApiClientOptions) {
    this.baseURL = options.baseURL;
    this.getToken = options.getToken;
    this.getRefreshToken = options.getRefreshToken;
    this.getClerkToken = options.getClerkToken;
    this.onTokenRefresh = options.onTokenRefresh;
    this.onSignOut = options.onSignOut;
    this.updateApolloToken = options.updateApolloToken;
  }

  private getTokenDataFromLocalStorage(): any {
    return JSON.parse(localStorage.getItem('token_data') || '{}');
  }

  private async performTokenRefresh(): Promise<any> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.onSignOut();
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${this.baseURL}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `mutation RefreshToken($refreshToken: String!) { refreshToken(refreshToken: $refreshToken) }`,
          variables: { refreshToken },
        }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const result = await response.json();

      if (result.errors) {
        throw new Error('Token refresh failed');
      }

      const tokenData = result.data.refreshToken;
      this.onTokenRefresh(tokenData);

      return tokenData;
    } catch (error) {
      console.error('Token refresh failed:', error);
      this.onSignOut();
      throw error;
    }
  }

  async fetch(url: string | URL | RequestInfo, options: RequestInit = {}): Promise<Response> {
    const token = this.getToken();

    // DefaultChatTransport may pass a Headers instance; spread requires a plain object
    const incomingHeaders =
      options.headers instanceof Headers
        ? Object.fromEntries(options.headers.entries())
        : ((options.headers as Record<string, string>) ?? {});

    // Build headers - try legacy token first, then Clerk
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...incomingHeaders,
    };

    delete headers['content-type'];
    delete headers['Authorization'];

    if (token) {
      // Legacy JWT token
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Fall back to Clerk token
      const clerkToken = await this.getClerkToken();
      if (clerkToken) {
        headers['Authorization'] = `Bearer ${clerkToken}`;
        headers['X-Clerk-Token'] = 'true';
      }
    }

    let response = await fetch(url, {
      ...options,
      headers,
    });

    // If we get a 401, try to refresh the token and retry
    if (response.status === 401) {
      // Only try refresh if we're using legacy JWT (not Clerk)
      if (token) {
        try {
          console.log('🔄 Token expired, attempting refresh...');
          await this.performTokenRefresh();

          // Retry with new legacy token
          const tokenData = this.getTokenDataFromLocalStorage();
          const newToken = tokenData.access_token;

          // Update Apollo Client's token if available
          if (this.updateApolloToken && newToken) {
            this.updateApolloToken(newToken);
          }

          const retryHeaders = {
            'Content-Type': 'application/json',
            ...incomingHeaders,
            ...(newToken && { Authorization: `Bearer ${newToken}` }),
          };

          console.log('🔄 Retrying request with new token...');
          response = await fetch(url, {
            ...options,
            headers: retryHeaders,
          });

          console.log('✅ Request retry successful');
        } catch (error) {
          console.error('❌ Token refresh failed, signing out');
          this.onSignOut();
          throw error;
        }
      } else {
        // Using Clerk auth - no refresh token available, just sign out
        console.error('❌ Clerk authentication failed, signing out');
        this.onSignOut();
        throw new Error('Authentication failed');
      }
    }

    return response;
  }
}

// Hook to create and use the API client
export function useApiClient() {
  const { token, signOut, client, getClerkToken } = useAuth();

  const getTokenDataFromLocalStorage = () => {
    return JSON.parse(localStorage.getItem('token_data') || '{}');
  };

  const apiClient = new ApiClient({
    baseURL: import.meta.env.VITE_API_URL,
    getToken: () => token,
    getRefreshToken: () => {
      const tokenData = getTokenDataFromLocalStorage();
      return tokenData.refresh_token || null;
    },
    getClerkToken: getClerkToken,
    onTokenRefresh: (tokenData) => {
      // Update localStorage directly to avoid React re-renders
      const currentTokenData = getTokenDataFromLocalStorage();
      const updatedTokenData = {
        ...currentTokenData,
        ...tokenData,
      };
      localStorage.setItem('token_data', JSON.stringify(updatedTokenData));
    },
    onSignOut: signOut,
    updateApolloToken: client?.updateToken,
  });

  return apiClient;
}
